import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { articles, getArticlesByChannel, getFeaturedArticle } from "@/mocks/articles";
import { faqs } from "@/mocks/faqs";
import ArticleCard from "@/components/feature/ArticleCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SectionHeading from "@/components/base/SectionHeading";
import HeroSection from "./components/HeroSection";
import HotList from "./components/HotList";
import ChannelGrid from "./components/ChannelGrid";
import ChannelListCard from "./components/ChannelListCard";

const SITE = "https://zozxnbl.cn";

export default function Home() {
  const featured = getFeaturedArticle();
  const investigations = getArticlesByChannel("shen-du-diao-cha");
  const faqHome = faqs.slice(0, 5);

  useSeo({
    title: "51爆料网 | 栏目导航·内容板块入口·站点导览",
    description:
      "51爆料网栏目导航页汇总首页栏目、专题频道与内容板块入口，方便快速定位爆料分类。深度调查、实时热点与事件专题一页直达，找内容不再绕路，站点导览清晰高效。",
    keywords: "51爆料网,栏目导航,内容板块入口,站点导览,深度调查",
    canonical: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "51爆料网",
        url: SITE,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE}/lanmu`,
          "query-input": "required name=keyword",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "51爆料网",
        url: SITE,
        email: "hello@51baoliao.com",
        description:
          "以栏目导航为入口的内容门户，聚合深度调查、实时热点、消费爆料、辟谣科普、行业观察与生活警示等内容板块。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqHome.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ],
  });

  return (
    <>
      <HeroSection />

      <section className="mx-auto max-w-[1280px] px-6 py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHeading
              eyebrow="编辑精选"
              title="本期重点推荐"
              desc="由编辑团队从近期内容中挑选，兼顾时效与信息密度。"
            />
            <ArticleCard article={featured} variant="featured" />
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {articles
                .filter((a) => a.slug !== featured.slug)
                .slice(0, 2)
                .map((a) => (
                  <ArticleCard key={a.slug} article={a} variant="default" />
                ))}
            </div>
          </div>
          <aside className="lg:col-span-4">
            <HotList />
            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="flex items-center gap-2 text-sm font-medium text-accent-900">
                <i className="ri-compass-3-line" />
                不知道从哪看起？
              </p>
              <p className="mt-2 text-sm leading-relaxed text-accent-800">
                先打开栏目导航页，所有首页栏目、专题频道与内容板块入口都集中在那里，一步步定位即可。
              </p>
              <Link
                to="/lanmu"
                className="mt-4 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
              >
                打开站点导览
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-6 py-14">
          <SectionHeading
            eyebrow="栏目板块"
            title="按板块找到你要的内容"
            desc="八个常设栏目分为首页栏目、专题频道与内容板块三类，点击任意板块进入对应频道页。"
            moreTo="/lanmu"
            moreLabel="查看完整导航"
          />
          <ChannelGrid />
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 py-14">
        <SectionHeading
          eyebrow="深度调查"
          title="慢一点，看得更清楚"
          desc="用数周时间走访与比对，还原事件背后的完整链路。"
          moreTo="/pindao/shen-du-diao-cha"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {investigations.slice(0, 3).map((a) => (
            <ArticleCard key={a.slug} article={a} variant="default" />
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-6 py-14">
          <SectionHeading
            eyebrow="消费与真相"
            title="和你的钱包与判断有关"
            desc="把消费里的坑说清楚，把流行的说法先求证一遍。"
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChannelListCard channelSlug="xiao-fei-bao-liao" limit={4} />
            <ChannelListCard channelSlug="pi-yao-ke-pu" limit={4} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="常见问题" title="使用 51爆料网前，先看这几条" />
            <FaqAccordion items={faqHome} />
          </div>
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="生活警示" title="替家人多看一眼" moreTo="/pindao/sheng-huo-jing-shi" />
            <ChannelListCard channelSlug="sheng-huo-jing-shi" limit={4} />
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-foreground-950">
        <div className="mx-auto flex max-w-[1280px] flex-col items-start justify-between gap-6 px-6 py-12 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              想快速定位内容？从站点导览开始
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-300">
              栏目导航页汇总了全部首页栏目、专题频道与内容板块入口，一页即可找到你需要的分类。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/lanmu"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-compass-3-line" />
              打开栏目导航
            </Link>
            <Link
              to="/changjianwenti"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-700 px-6 py-3 text-sm font-medium text-background-100 transition-colors hover:bg-foreground-900"
            >
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}